// The words and code of the webhook reference (B6), kept apart from the
// component so they can be tested for what they claim: the snippets are run
// against a signature made the way the server makes one, and every number in
// the contract is the server's own, sent with the catalog.
import type { WebhookEventSpec } from '../../api/webhooks';

/** The numbers the delivery job runs on, as `GET /admin/webhooks` reports them. */
export interface WebhookContract {
  apiVersion: string;
  /** every try, the first included */
  tries: number;
  /** the wait before each try after the first */
  retrySeconds: number[];
  timeoutSeconds: number;
  disableAfter: number;
  keepDays: number;
  /** how old a signature's timestamp may be */
  toleranceSeconds: number;
  documentLinkHours: number;
}

/** What the page says until the server has answered, and what a server that predates the field ran on. */
export const DEFAULT_CONTRACT: WebhookContract = {
  apiVersion: '2026-10-09', tries: 6, retrySeconds: [30, 120, 600, 3600, 21_600], timeoutSeconds: 10,
  disableAfter: 20, keepDays: 30, toleranceSeconds: 300, documentLinkHours: 24,
};

/** "30 seconds", "2 minutes", "6 hours". */
export function spanWords(seconds: number): string {
  const [n, unit] = seconds % 3600 === 0 ? [seconds / 3600, 'hour'] : seconds % 60 === 0 ? [seconds / 60, 'minute'] : [seconds, 'second'];
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/** "about 8 hours" — the whole schedule, rounded to what a person would say. */
export function scheduleSpan(retrySeconds: number[]): string {
  const total = retrySeconds.reduce((a, b) => a + b, 0);
  if (total >= 5400) return `about ${Math.round(total / 3600)} hours`;
  if (total >= 90) return `about ${Math.round(total / 60)} minutes`;
  return spanWords(total);
}

/** The delivery contract, one promise a line. */
export function contractLines(c: WebhookContract): { title: string; text: string }[] {
  return [
    { title: 'At least once', text: 'The same event can arrive more than once. Keep its id and ignore one you have already handled.' },
    { title: 'No order', text: 'Events can arrive out of order. Use createdAt, not arrival, to decide which is newer.' },
    { title: `Answer within ${spanWords(c.timeoutSeconds)}`, text: 'Reply with any 2xx status as soon as the request is stored, and do the work afterwards. A redirect is not followed.' },
    {
      title: `${c.tries} tries over ${scheduleSpan(c.retrySeconds)}`,
      text: `A failure is tried again after ${c.retrySeconds.map(spanWords).join(', ')}. A 4xx answer other than 408 or 429 is not retried.`,
    },
    { title: '410 means stop', text: 'Answer 410 Gone and that event is not sent again.' },
    {
      title: `Switched off after ${c.disableAfter}`,
      text: `When ${c.disableAfter} events in a row are given up on, the endpoint is switched off and the account's owner is emailed. Switch it back on and use Replay all failed to catch up.`,
    },
    { title: `${c.keepDays} days of history`, text: `Events and their delivery log are kept for ${c.keepDays} days, and any of them can be sent again from the log.` },
  ];
}

/** The headers every request carries. */
export const WEBHOOK_HEADERS: { name: string; text: string }[] = [
  { name: 'X-FM-Signature', text: 't=<unix seconds>,v1=<hex> — an HMAC-SHA256 of t + "." + the raw body, keyed with the endpoint’s signing secret.' },
  { name: 'X-FM-Event', text: 'The event’s type, as in the list below.' },
  { name: 'X-FM-Event-Id', text: 'The event’s id — the same on every retry and replay. Dedupe on this.' },
  { name: 'X-FM-Timestamp', text: 'The same unix seconds the signature was made with.' },
];

export const nodeSnippet = (toleranceSeconds: number): string => `const crypto = require('crypto');

// Check the RAW request body, before any JSON parsing.
function isFromFormMagic(rawBody, signatureHeader, secret) {
  const parts = Object.fromEntries(
    String(signatureHeader || '').split(',').map((part) => part.split('=')));
  const t = parts.t || '';
  const v1 = parts.v1 || '';
  // A request older than ${spanWords(toleranceSeconds)} is a replay.
  if (!/^\\d+$/.test(t) || Math.abs(Date.now() / 1000 - Number(t)) > ${toleranceSeconds}) return false;
  const expected = crypto.createHmac('sha256', secret).update(t + '.' + rawBody).digest('hex');
  // Constant-time compare: never ===.
  return expected.length === v1.length
    && crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(v1));
}`;

export const pythonSnippet = (toleranceSeconds: number): string => `import hashlib
import hmac
import time


def is_from_form_magic(raw_body: bytes, signature_header: str, secret: str) -> bool:
    """Check the RAW request body, before any JSON parsing."""
    parts = dict(p.split("=", 1) for p in (signature_header or "").split(",") if "=" in p)
    t, v1 = parts.get("t", ""), parts.get("v1", "")
    # A request older than ${spanWords(toleranceSeconds)} is a replay.
    if not t.isdigit() or abs(time.time() - int(t)) > ${toleranceSeconds}:
        return False
    expected = hmac.new(secret.encode(), t.encode() + b"." + raw_body, hashlib.sha256).hexdigest()
    # Constant-time compare: never ==.
    return hmac.compare_digest(expected, v1)`;

/** One event as it arrives: the envelope around the catalog's own sample. */
export function exampleEnvelope(e: WebhookEventSpec, apiVersion: string): Record<string, unknown> {
  return {
    id: 'evt_3f9c1d2a7b6e4f508a1c2d3e',
    type: e.type,
    apiVersion,
    createdAt: '2026-10-09T14:03:01.000Z',
    account: { id: 12, slug: 'acme' },
    data: e.sample,
  };
}
